import { type SetStateAction, useCallback } from "react";
import { create } from "zustand";
import { isEqual } from "lodash";
import {
  emptySkillValidationErrors,
  SkillValidationErrors,
} from "@src/features/libraryItem/skill/utils/validation";
import {
  SkillBuilder_SkillFragment,
  SkillBuilder_SkillVersionFragment,
  SkillBuilder_VerificationStepFragment,
} from "../SkillDetailPage.generated";

export type SetSkillValidationErrorsFn = (
  fn: SetStateAction<SkillValidationErrors>,
) => void;

export const EMPTY_SKILL_BUILDER_VERIFICATION_STEPS: SkillBuilder_VerificationStepFragment[] =
  [];

type SkillBuilderStoreValues = {
  skillId: number | undefined;
  skillVersion: SkillBuilder_SkillVersionFragment | null | undefined;
  validationErrors: SkillValidationErrors;
  draftVersionSelected: boolean;
  publishedVersionSelected: boolean;
};

export type SkillBuilderStoreState = SkillBuilderStoreValues & {
  setSkillVersion: (
    skillVersion: SetStateAction<
      SkillBuilder_SkillVersionFragment | null | undefined
    >,
  ) => void;
  setValidationErrors: SetSkillValidationErrorsFn;
  resetSkillBuilderStore: () => void;
};

type ActiveStep = { id: string; popMenu: boolean } | null;

type SkillBuilderActiveStepStoreState = {
  activeStep: ActiveStep;
  setActiveStep: (next: ActiveStep) => void;
  resetActiveStep: () => void;
};

const isSameActiveStep = (a: ActiveStep, b: ActiveStep): boolean =>
  a === b || (a?.id === b?.id && a?.popMenu === b?.popMenu);

const useSkillBuilderActiveStepStore = create<SkillBuilderActiveStepStoreState>(
  (set) => ({
    activeStep: null,
    setActiveStep: (next) => {
      set((state) =>
        isSameActiveStep(state.activeStep, next) ? state : { activeStep: next },
      );
    },
    resetActiveStep: () => {
      set((state) =>
        state.activeStep === null ? state : { activeStep: null },
      );
    },
  }),
);

export const useActiveSkillBuilderStepId = (): string | null =>
  useSkillBuilderActiveStepStore((state) => state.activeStep?.id ?? null);

export const useIsActiveSkillBuilderStep = (
  stepId: string | null | undefined,
): boolean =>
  useSkillBuilderActiveStepStore(
    (state) => !!stepId && state.activeStep?.id === stepId,
  );

/**
 * Whether the given step is the active step AND its activation was
 * deliberate (deserves the context-menu auto-open). Returns false for
 * incidental activations (button clicks, dropdown trigger, etc.) so
 * those don't pop the menu.
 */
export const useIsMenuAutoOpenable = (
  stepId: string | null | undefined,
): boolean =>
  useSkillBuilderActiveStepStore(
    (state) =>
      !!stepId &&
      state.activeStep?.id === stepId &&
      state.activeStep.popMenu === true,
  );

/**
 * Activate a step (or pass null to deactivate). By default the activation
 * is treated as deliberate — the step's context menu pops. Used for
 * step-body clicks, outline clicks, and the add/duplicate/conditional-
 * group dispatchers.
 *
 * Pass `{ popMenu: false }` for incidental activations — clicks on text
 * fields or action buttons inside a step that bubble to the wrapper, or
 * the dropdown trigger (which opens the menu via Radix's
 * `openedViaTrigger`).
 *
 * This is the only public way to activate a step from outside the store.
 */
export const useActivateStep = (): ((
  stepId: string | null,
  opts?: { popMenu?: boolean },
) => void) => {
  const setActiveStep = useSkillBuilderActiveStepStore(
    (state) => state.setActiveStep,
  );
  return useCallback(
    (stepId, opts) => {
      setActiveStep(
        stepId === null
          ? null
          : { id: stepId, popMenu: opts?.popMenu !== false },
      );
    },
    [setActiveStep],
  );
};

export const useActiveSkillBuilderStep = (
  skip?: boolean,
): SkillBuilder_VerificationStepFragment | undefined => {
  const activeStepId = useSkillBuilderActiveStepStore((state) =>
    skip ? null : state.activeStep?.id ?? null,
  );
  return useSkillBuilderStore((state) =>
    activeStepId
      ? state.skillVersion?.verificationSteps.find(
          (step) => step.id === activeStepId,
        )
      : undefined,
  );
};

export const useActiveSkillBuilderStepPosition = (): number | null => {
  const activeStepId = useActiveSkillBuilderStepId();
  return useSkillBuilderStore((state) => {
    if (!activeStepId) return null;
    const activeStepPosition = state.skillVersion?.verificationSteps.findIndex(
      (step) => step.id === activeStepId,
    );
    return activeStepPosition === undefined || activeStepPosition === -1
      ? null
      : activeStepPosition;
  });
};

const resolveSetStateAction = <T>(action: SetStateAction<T>, value: T): T => {
  return typeof action === "function"
    ? (action as (value: T) => T)(value)
    : action;
};

const getSkillBuilderStoreValues = ({
  skillVersion,
  validationErrors,
}: Pick<
  SkillBuilderStoreValues,
  "skillVersion" | "validationErrors"
>): SkillBuilderStoreValues => {
  return {
    skillId: skillVersion?.skill.id,
    skillVersion,
    validationErrors,
    draftVersionSelected:
      skillVersion?.id === skillVersion?.skill.draftVersion?.id,
    publishedVersionSelected:
      skillVersion?.id === skillVersion?.skill.publishedVersion?.id,
  };
};

const getInitialSkillBuilderStoreValues = () =>
  getSkillBuilderStoreValues({
    skillVersion: undefined,
    validationErrors: emptySkillValidationErrors(),
  });

const preserveSkillVersionReferences = (
  prevSkillVersion: SkillBuilder_SkillVersionFragment | null | undefined,
  nextSkillVersion: SkillBuilder_SkillVersionFragment | null | undefined,
): SkillBuilder_SkillVersionFragment | null | undefined => {
  if (!prevSkillVersion || !nextSkillVersion) return nextSkillVersion;
  if (prevSkillVersion.id !== nextSkillVersion.id) return nextSkillVersion;

  const verificationSteps = preserveVerificationStepReferences(
    prevSkillVersion.verificationSteps,
    nextSkillVersion.verificationSteps,
  );

  return {
    ...nextSkillVersion,
    skill: preserveSkillReferences(
      prevSkillVersion.skill,
      nextSkillVersion.skill,
    ),
    headerMedia: isEqual(
      prevSkillVersion.headerMedia,
      nextSkillVersion.headerMedia,
    )
      ? prevSkillVersion.headerMedia
      : nextSkillVersion.headerMedia,
    headerLibraryItemLink: isEqual(
      prevSkillVersion.headerLibraryItemLink,
      nextSkillVersion.headerLibraryItemLink,
    )
      ? prevSkillVersion.headerLibraryItemLink
      : nextSkillVersion.headerLibraryItemLink,
    verificationSteps,
  };
};

const preserveSkillReferences = (
  prevSkill: SkillBuilder_SkillFragment,
  nextSkill: SkillBuilder_SkillFragment,
): SkillBuilder_SkillFragment => {
  if (isEqual(prevSkill, nextSkill)) return prevSkill;

  const libraryItem = isEqual(prevSkill.libraryItem, nextSkill.libraryItem)
    ? prevSkill.libraryItem
    : {
        ...nextSkill.libraryItem,
        name: isEqual(prevSkill.libraryItem.name, nextSkill.libraryItem.name)
          ? prevSkill.libraryItem.name
          : nextSkill.libraryItem.name,
        description: isEqual(
          prevSkill.libraryItem.description,
          nextSkill.libraryItem.description,
        )
          ? prevSkill.libraryItem.description
          : nextSkill.libraryItem.description,
        coverImage: isEqual(
          prevSkill.libraryItem.coverImage,
          nextSkill.libraryItem.coverImage,
        )
          ? prevSkill.libraryItem.coverImage
          : nextSkill.libraryItem.coverImage,
        createdBy: isEqual(
          prevSkill.libraryItem.createdBy,
          nextSkill.libraryItem.createdBy,
        )
          ? prevSkill.libraryItem.createdBy
          : nextSkill.libraryItem.createdBy,
      };

  return {
    ...nextSkill,
    libraryItem,
    draftVersion: isEqual(prevSkill.draftVersion, nextSkill.draftVersion)
      ? prevSkill.draftVersion
      : nextSkill.draftVersion,
    publishedVersion: isEqual(
      prevSkill.publishedVersion,
      nextSkill.publishedVersion,
    )
      ? prevSkill.publishedVersion
      : nextSkill.publishedVersion,
  };
};

const preserveVerificationStepReferences = (
  prevSteps: SkillBuilder_VerificationStepFragment[],
  nextSteps: SkillBuilder_VerificationStepFragment[],
): SkillBuilder_VerificationStepFragment[] => {
  const prevStepsById = new Map(prevSteps.map((step) => [step.id, step]));
  const nextStepsWithPreservedReferences = nextSteps.map((nextStep) => {
    const prevStep = prevStepsById.get(nextStep.id);
    return prevStep && isEqual(prevStep, nextStep) ? prevStep : nextStep;
  });

  return prevSteps.length === nextStepsWithPreservedReferences.length &&
    nextStepsWithPreservedReferences.every(
      (step, index) => step === prevSteps[index],
    )
    ? prevSteps
    : nextStepsWithPreservedReferences;
};

const useSkillBuilderStore = create<SkillBuilderStoreState>((set, get) => ({
  ...getInitialSkillBuilderStoreValues(),
  setSkillVersion: (nextSkillVersion) => {
    const prevSkillVersionId = get().skillVersion?.id;
    set((state) => {
      const skillVersion = preserveSkillVersionReferences(
        state.skillVersion,
        resolveSetStateAction(nextSkillVersion, state.skillVersion),
      );

      return getSkillBuilderStoreValues({
        skillVersion,
        validationErrors: state.validationErrors,
      });
    });
    const currentSkillVersion = get().skillVersion;
    const activeStepStore = useSkillBuilderActiveStepStore.getState();
    if (currentSkillVersion?.id !== prevSkillVersionId) {
      activeStepStore.resetActiveStep();
      return;
    }
    const activeStepId = activeStepStore.activeStep?.id;
    if (
      activeStepId &&
      !currentSkillVersion?.verificationSteps.some(
        (step) => step.id === activeStepId,
      )
    ) {
      activeStepStore.resetActiveStep();
    }
  },
  setValidationErrors: (nextValidationErrors) => {
    set((state) => ({
      validationErrors: resolveSetStateAction(
        nextValidationErrors,
        state.validationErrors,
      ),
    }));
  },
  resetSkillBuilderStore: () => {
    useSkillBuilderActiveStepStore.getState().resetActiveStep();
    set(getInitialSkillBuilderStoreValues());
  },
}));

export default useSkillBuilderStore;
